import { useNavigate } from "react-router-dom";
import { useState,useEffect } from "react";
import { Typography, Form, Flex, Image, Alert } from "antd";
import CustomButton from "../../components/CustomButton/Button";
import CustomInput from "../../components/CustomInput/Input";
import PasswordInput from "../../components/Auth/PasswordInput";
import Logo from "../../assets/images/logo.png";
import {
  GLOBAL_CONSTANTS,
  ERROR_MSGS,
  BTN_TXT,
} from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  loginUser,
  loginUserBeforeOtp,
  loginUserAfterOtp,
  loginUserBeforeAuthenticator,
  loginUserAfterAuthenticator,
  clearAuthError,
} from "../../redux/slices/AuthSlice";
import CryptoJS from "crypto-js";
import "./Login.scss";

const { Text } = Typography;

const INITIAL_AUTH_STEP = {
  mode: "credentials",
  email: "",
  password: "",
  setupToken: null,
  qrCodeDataUrl: null,
  enrolled: false,
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [isOtp, setIsOtp] = useState({
    otp: false,
    reqId: null,
    email: "",
    password: "",
  });
  const [authStep, setAuthStep] = useState(INITIAL_AUTH_STEP);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [sessionRevokedNote, setSessionRevokedNote] = useState(null);
  const { loading, error } = useSelector((state) => state.auth);
  const authenticatorEnabled =
    process.env.REACT_APP_AUTHENTICATOR_ENABLED === "true";
  const otpEnabled =
    !authenticatorEnabled && process.env.REACT_APP_OTP_ENABLED === "true";

  const toggleVisibility = () => {
    setVisible((prev) => !prev);
  };

  useEffect(() => {
    const notice = sessionStorage.getItem("sessionRevokedMessage");
    if (notice) {
      setSessionRevokedNote(notice);
      sessionStorage.removeItem("sessionRevokedMessage");
    }
  }, []);

   useEffect(() => {
      return () => {
        dispatch(clearAuthError());
      };
    }, [dispatch]);

  const startResendTimer = () => {
    let counter = 30;
    setCanResend(false);
    setResendTimer(counter);

    const interval = setInterval(() => {
      counter -= 1;
      setResendTimer(counter);
      if (counter === 0) {
        setCanResend(true);
        clearInterval(interval);
      }
    }, 1000);
  };

  const resetAuthFlow = () => {
    dispatch(clearAuthError());
    form.resetFields();
    setIsOtp({ otp: false, reqId: null, email: "", password: "" });
    setAuthStep(INITIAL_AUTH_STEP);
    setCanResend(false);
    setResendTimer(30);
  };

  const handleAuthenticatorStep = async (values) => {
    const hashed = CryptoJS.SHA256(values.password).toString();

    if (authStep.mode === "credentials") {
      const res = await dispatch(
        loginUserBeforeAuthenticator({
          email: values.email,
          password: hashed,
        })
      );

      if (res.payload?.statusCode !== 201) {
        return;
      }

      const response = res.payload.response || {};
      form.resetFields(["totp"]);

      if (response.enrolled) {
        setAuthStep({
          mode: "verify",
          email: values.email,
          password: hashed,
          setupToken: null,
          qrCodeDataUrl: null,
          enrolled: true,
        });
        return;
      }

      setAuthStep({
        mode: "enroll",
        email: values.email,
        password: hashed,
        setupToken: response.setupToken,
        qrCodeDataUrl: response.qrCodeDataUrl,
        enrolled: false,
      });
      return;
    }

    const payload = {
      email: authStep.email,
      password: authStep.password,
      totp: values.totp,
    };

    if (authStep.mode === "enroll" && authStep.setupToken) {
      payload.setupToken = authStep.setupToken;
    }

    const res = await dispatch(loginUserAfterAuthenticator(payload));

    if (res.payload?.statusCode === 201) {
      resetAuthFlow();
      navigate("/enquiry-management");
    }
  };

  const handleSubmit = async (values) => {
    const hashed = CryptoJS.SHA256(values.password).toString();

    if (authenticatorEnabled) {
      await handleAuthenticatorStep(values);
      return;
    }

    if (otpEnabled) {
      let payload;
      let action;
      if (isOtp.otp) {
        payload = {
          email: isOtp.email,
          password: isOtp.password,
          otp: values.otp,
          reqId: isOtp.reqId,
        };
        action = loginUserAfterOtp;
      } else {
        payload = {
          email: values.email,
          password: hashed,
        };
        action = loginUserBeforeOtp;
      }

      const res = await dispatch(action(payload));

      if (res.payload?.statusCode === 201) {
        form.resetFields();
        if (isOtp.otp) {
          navigate("/enquiry-management");
        } else {
          setIsOtp({
            otp: true,
            reqId: res.payload.response.reqId,
            email: values.email,
            password: hashed,
          });
          setCanResend(false);
          setResendTimer(30);
          startResendTimer();
        }
      }
      return;
    }

    const res = await dispatch(
      loginUser({
        email: values.email,
        password: hashed,
      })
    );

    if (res.payload?.statusCode === 201) {
      form.resetFields();
      navigate("/enquiry-management");
    }
  };

  const showAuthenticatorStep =
    authenticatorEnabled && authStep.mode !== "credentials";
  const showEmailOtpStep = otpEnabled && isOtp.otp;
  const submitLabel = showAuthenticatorStep || showEmailOtpStep
    ? BTN_TXT.SUBMIT
    : BTN_TXT.LOGIN;

  return (
    <div className="login-container">
      <div className="auth-container">
        <div className="logo">
          <Image preview={false} src={Logo} alt="Logo" />
        </div>

        <Form
          form={form}
          layout="vertical"
          className="login-form"
          onFinish={handleSubmit}
          initialValues={{ remember: true }}
        >
          {showAuthenticatorStep ? (
            <>
              {authStep.mode === "enroll" && authStep.qrCodeDataUrl && (
                <div className="authenticator-panel">
                  <Text strong className="authenticator-title">
                    Set up Microsoft Authenticator
                  </Text>
                  <Text className="authenticator-hint">
                    Scan this QR code in Microsoft Authenticator, then enter the
                    6-digit code below.
                  </Text>
                  <Image
                    preview={false}
                    src={authStep.qrCodeDataUrl}
                    alt="Microsoft Authenticator QR code"
                    width={168}
                    className="authenticator-qr"
                  />
                </div>
              )}

              {authStep.mode === "verify" && (
                <div className="authenticator-verify">
                  <Text className="authenticator-verify-text">
                    Enter the 6-digit code from Microsoft Authenticator for{" "}
                    {authStep.email}.
                  </Text>
                  <Text className="authenticator-verify-help">
                    Lost authenticator? Contact your super-admin.
                  </Text>
                </div>
              )}

              <CustomInput
                label="Authenticator Code"
                name="totp"
                type="text"
                placeholder="Enter 6-digit code"
                rules={[
                  {
                    required: true,
                    message: ERROR_MSGS.OTP_ERR_MSG,
                  },
                  {
                    pattern: /^\d{6}$/,
                    message: "Enter a valid 6-digit code",
                  },
                ]}
              />
            </>
          ) : showEmailOtpStep ? (
            <>
              <CustomInput
                label="OTP"
                name="otp"
                type="text"
                placeholder="Enter OTP"
                rules={[
                  {
                    required: true,
                    message: ERROR_MSGS.OTP_ERR_MSG,
                  },
                ]}
              />

              <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
                <Text type="secondary">
                  {canResend
                    ? "Didn’t receive the OTP?"
                    : `Resend available in ${resendTimer}s`}
                </Text>
                <CustomButton
                  type="link"
                  disabled={!canResend}
                  onClick={async () => {
                    setCanResend(false);
                    setResendTimer(30);
                    startResendTimer();

                    const resendRes = await dispatch(
                      loginUserBeforeOtp({
                        email: isOtp.email,
                        password: isOtp.password,
                      })
                    );

                    if (resendRes.payload?.statusCode === 201) {
                      setIsOtp((prev) => ({
                        ...prev,
                        reqId: resendRes.payload.response.reqId,
                      }));
                    }
                  }}
                >
                  Resend OTP
                </CustomButton>
              </Flex>
            </>
          ) : (
            <>
              <CustomInput
                label="Email"
                name="email"
                type="email"
                placeholder="Your Email"
                rules={[
                  {
                    required: true,
                    message: ERROR_MSGS.EMAIL_ERR_MSG,
                  },
                ]}
              />

              <PasswordInput
                label="Password"
                name="password"
                visible={visible}
                onToggleVisibility={toggleVisibility}
                placeholder="Password"
                rules={[
                  {
                    required: true,
                    message: ERROR_MSGS.PWD_ERR_MSG,
                  },
                ]}
              />

              <Flex justify="flex-end" style={{ marginBottom: 20 }}>
                <Text
                  onClick={() => navigate("/forgot-password")}
                  className="forgot-password-link"
                >
                  {GLOBAL_CONSTANTS.FORGOT_PWD}
                </Text>
              </Flex>
            </>
          )}

          {sessionRevokedNote && (
            <Alert
              style={{ marginBottom: 20 }}
              message={sessionRevokedNote}
              type="warning"
              showIcon
              closable
              onClose={() => setSessionRevokedNote(null)}
            />
          )}

          {error && (
            <Alert
              style={{ marginBottom: 20 }}
              message={error}
              type="error"
              showIcon
            />
          )}

          <Form.Item>
            <CustomButton
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{ padding: 24 }}
            >
              {submitLabel}
            </CustomButton>
          </Form.Item>

          {(showAuthenticatorStep || showEmailOtpStep) && (
            <CustomButton type="text" block onClick={resetAuthFlow}>
              {`${BTN_TXT.BACK} To Login`}
            </CustomButton>
          )}
        </Form>
      </div>
    </div>
  );
};

export default Login;
